// useGlobalSearch.ts — TanStack Query hook for the global (⌘K) search
// endpoint. Keyed by the (already debounced) term, so a superseded term's
// slow response lands in its own cache entry and can never overwrite the
// latest query's render — stale-response discard for free. Disabled below
// the server's 3-character minimum: the endpoint 400s there, and the palette
// shows a hint instead of ever provoking that error.

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from './client';
import { resolvePath } from './paths';
import { searchResponseToUi, type SearchGroupUi, type SearchResponseDto } from './searchAdapters';

/** Mirrors the server default (apps/search SEARCH_MIN_QUERY_LENGTH). */
export const SEARCH_MIN_QUERY_LENGTH = 3;

export function useGlobalSearch(term: string) {
  const resolved = resolvePath('/search');
  const q = term.trim();
  return useQuery({
    queryKey: ['search', 'global', q, resolved?.path ?? 'noop'],
    enabled: resolved !== null && q.length >= SEARCH_MIN_QUERY_LENGTH,
    staleTime: 30_000,
    queryFn: async (): Promise<SearchGroupUi[]> => {
      if (!resolved) return [];
      const dto = await apiFetch<SearchResponseDto>(resolved.path, { query: { q } });
      return searchResponseToUi(dto);
    },
  });
}
